import React, { useContext, useEffect, useState } from "react";
import { forEach } from "lodash";
import dayjs from "dayjs";

import { Typography } from "@/components/Typography";
import { Form } from "@/components/Form";

import { QREditorContext } from "@/contexts/QREditorContext/QrEditorContext";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";

import { isFileAblob, onBindSocialNetworks, onBindThemePicker } from "@/utils/qrEditorHelper";
import { qrCouponValidation } from "@/utils/validators/qr-coupon";
import { applyQRFormDefaultValues, onBindClick } from "@/utils/formHelper";

import { FileProps } from "@/components/FilesUpload/types";
import { CouponDateTimeType } from "@/types/qr";
import { QRCouponProps } from "./types";

import { useTranslation } from "react-i18next";
import { NAMESPACES } from "@/internals/constants/namespaces";
import "./styles.scss";

export function QRCoupon({ data }: QRCouponProps) {
  const { t } = useTranslation(NAMESPACES.VALIDATIONS);
  const { qrData, setQrData } = useContext<QREditorContextProps<"coupon">>(QREditorContext);

  const [qrCouponData, setQrCouponData] = useState<QRProps<"coupon">>(qrData);
  const [renderForm, setRenderForm] = useState(false);
  const dataCouponWithDefaultValues = applyQRFormDefaultValues(data, qrData.data);

  const omittedValues = React.useCallback(() => {
    const values = [];
    forEach(qrData.data.logoUrl, (image, idx) => {
      isFileAblob(image.url) && values.push(`data.logoUrl[${idx}].url`);
    });

    return ["data.file", ...values];
  }, [qrData.data.logoUrl]);

  onBindClick(dataCouponWithDefaultValues, "qrCouponUploadImage", (files: FileProps[]) => {
    setQrData(
      {
        ...qrData,
        data: {
          ...qrData.data,
          logoUrl: files,
        },
      },
      omittedValues()
    );
  });

  onBindClick(dataCouponWithDefaultValues, "couponDateTime", (dateTimeData: CouponDateTimeType) => {
    return setQrData(
      {
        ...qrData,
        data: {
          ...qrData.data,
          couponDateTime: {
            couponValidDate: dayjs(
              dateTimeData?.couponValidDate || qrData.data.couponDateTime?.couponValidDate
            ).format("YYYY/MM/DD"),
            couponTimezone:
              dateTimeData?.couponTimezone || qrData.data.couponDateTime?.couponTimezone,
          },
        },
      },
      omittedValues()
    );
  });

  onBindThemePicker(dataCouponWithDefaultValues, "qrCouponThemePicker", omittedValues());

  onBindSocialNetworks(
    dataCouponWithDefaultValues,
    "qrCouponSocials",
    "socialNetworks",
    omittedValues()
  );

  const onUpdateState = ({ field, value }) => {
    if (field === "file" || field == "" || field == "couponDateTime") return;
    setQrData(
      {
        ...qrData,
        data: {
          ...qrData.data,
          [field]: value,
        },
      },
      omittedValues()
    );
  };

  useEffect(() => {
    setQrCouponData(qrData);
  }, [qrData]);

  useEffect(() => {
    setQrData({ ...qrCouponData, isValidForm: renderForm });
  }, [renderForm]);

  return (
    <div className="qr-coupon">
      <Typography
        Tag="p"
        size="xl"
        classname="u-bold u-spacing-5"
      >
        {data.title}
      </Typography>
      <Form
        fields={dataCouponWithDefaultValues}
        schema={qrCouponValidation(data, t)}
        onWatchFields={({ field, value }) => onUpdateState({ field, value })}
        isFormValid={(isValidForm) => setRenderForm(isValidForm)}
      />
    </div>
  );
}
